import React from 'react';
import PropTypes from 'prop-types';
import {IconDown, IconUp} from './Icons';
import {
  getStyle,
  getCellTypeFromID,
  getNextClipValue,
  getPreviousClipValue,
} from './GelBand.utils';
import {GelDragDropContext, splitIDs} from '../GelDragDrop.utils';
import {GEL_TYPE, GEL_DRAG_DROP, CELL_TYPE} from '../GelDragDrop.constants';

const KEY = {
  DOWN_ARROW: 40,
  UP_ARROW: 38,
};

const bandRef = React.createRef();

export function GelBand(props) {
  const {state, clipValues, onStripChange, onGelBandClick} = React.useContext(
    GelDragDropContext
  );

  const style = getStyle({state, props});

  const setDraggable = () => props.gelType === GEL_TYPE.DRAGGABLE;

  const onDragStart = (event) => {
    event.dataTransfer.setData(GEL_DRAG_DROP.GEL_ID, props.gelId);
    event.dataTransfer.setDragImage(
      event.target,
      event.target.clientWidth / 2,
      0
    );
  };

  const {cellType} = getCellTypeFromID(props.gelId);

  const onUpIconChange = () => {
    const [colId, prevCellId, gelId] = splitIDs(props.gelId);
    if (
      cellType === CELL_TYPE.TRASH_CAN ||
      clipValues[clipValues.length - 1] === props.value
    ) {
      onStripChange({
        colId,
        prevCellId,
        currentCellId: prevCellId - 1,
        gelId,
        value: cellType === CELL_TYPE.TRASH_CAN ? clipValues[0] : 0,
      });
    } else {
      const value = getNextClipValue(clipValues, props.value);
      onStripChange({
        colId,
        prevCellId,
        currentCellId: prevCellId,
        gelId,
        value,
      });
    }
  };

  const onDownIconChange = () => {
    const [colId, prevCellId, gelId] = splitIDs(props.gelId);
    if (cellType === CELL_TYPE.GEL_HOLDER || clipValues[0] === props.value) {
      props.gelType === GEL_TYPE.DRAGGABLE &&
        onStripChange({
          colId,
          prevCellId,
          currentCellId: prevCellId + 1,
          gelId,
          value:
            cellType === CELL_TYPE.GEL_HOLDER
              ? clipValues[clipValues.length - 1]
              : 0,
        });
    } else {
      const value = getPreviousClipValue(clipValues, props.value);
      props.gelType === GEL_TYPE.DRAGGABLE &&
        onStripChange({
          colId,
          prevCellId,
          currentCellId: prevCellId,
          gelId,
          value,
        });
    }
  };

  const isUpIcon = () =>
    props.selected &&
    (cellType === CELL_TYPE.TRASH_CAN || cellType === CELL_TYPE.GEL_PATH);

  const isDownIcon = () =>
    props.selected &&
    (cellType === CELL_TYPE.GEL_HOLDER || cellType === CELL_TYPE.GEL_PATH);

  const onKeyUp = (event) => {
    if (event.keyCode === KEY.DOWN_ARROW) onDownIconChange();
    if (event.keyCode === KEY.UP_ARROW) onUpIconChange();
  };

  const onClick = () =>
    props.gelType === GEL_TYPE.DRAGGABLE && onGelBandClick(props.gelId);

  React.useEffect(() => {
    if (bandRef && bandRef.current && props.selected) {
      bandRef.current.focus();
    }
  }, [bandRef]);

  return (
    <div
      style={style}
      ref={bandRef}
      onKeyUp={onKeyUp}
      onClick={onClick}
      onDragStart={onDragStart}
      draggable={setDraggable()}
      aria-label={`${props.value} bp`}
      tabIndex={setDraggable() ? -1 : null}
      className={`${props.className}${props.gelType.toLowerCase()}`}>
      <div className='band'>
        {isUpIcon() && (
          <IconUp className='band_iconup' onClick={onUpIconChange} />
        )}
        <div className='band_text'>{props.children}</div>
        {isDownIcon() && (
          <IconDown className='band_icondown' onClick={onDownIconChange} />
        )}
      </div>
    </div>
  );
}

GelBand.propTypes = {
  /**
   * Text to render inside GelStrip
   */
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) || null,
  /**
   * Classname to be set on component
   */
  className: PropTypes.string,
  /**
   * Unique Id for Gel
   */
  gelId: PropTypes.string,
  /**
   * Type of Gel-Strip
   */
  gelType: PropTypes.oneOf(Object.keys(GEL_TYPE)),
  /**
   * Height value of Gel
   */
  height: PropTypes.number,
  /**
   * Opacity value of Gel
   */
  opacity: PropTypes.number,
  /**
   * Postion value of Gel
   */
  value: PropTypes.number || null,
  /**
   * Boolean to set if get is selected
   */
  selected: PropTypes.bool,
};

GelBand.defaultProps = {
  selected: false,
};
